/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { Grid, Link, Typography2 } from '@coursera/cds-core';
import { ChevronNextIcon, ChevronPreviousIcon } from '@coursera/cds-icons';

import type { Maybe, NavigationElement } from 'bundles/tutorials/__generated__/baseContentfulTypes';

type NavigationLink = Maybe<Pick<NavigationElement, 'label' | 'route'>>;

type Props = {
  prev?: NavigationLink;
  next?: NavigationLink;
};

const styles = {
  container: css`
    margin: 50px 0;
    box-shadow: 0 1.6px 3.6px 0 hsl(0deg 0% 100% / 13.2%), 0 0.3px 0.9px 0 hsl(0deg 0% 100% / 10.8%);
  `,
  link: css`
    display: flex;
  `,
  prevIcon: css`
    display: flex;
    align-items: center;
    height: 100%;
    margin-right: 10px;

    svg {
      margin: 0 auto;
      display: block;
    }
  `,
  nextIcon: css`
    display: flex;
    align-items: center;
    height: 100%;
    margin-left: 10px;

    svg {
      margin: 0 auto;
      display: block;
    }
  `,
};

const Navigation = ({ prev, next }: Props) => {
  if (!prev && !next) {
    return null;
  }

  return (
    <Grid container justifyContent="space-between" css={styles.container}>
      {prev?.route && (
        <Grid
          item
          container
          xs={6}
          justifyContent="flex-start"
          wrap="nowrap"
          css={styles.link}
          component={Link}
          href={prev.route}
          variant="quiet"
          typographyVariant="bodyPrimary"
        >
          <Grid item css={styles.prevIcon}>
            <ChevronPreviousIcon />
          </Grid>
          <Grid item>
            <Typography2 component="p" variant="bodySecondary" color="body">
              Previous
            </Typography2>
            <Typography2 component="p" variant="bodyPrimary" color="highlightBlue">
              {prev.label}
            </Typography2>
          </Grid>
        </Grid>
      )}
      {next?.route && (
        <Grid
          item
          container
          xs={6}
          justifyContent="flex-end"
          wrap="nowrap"
          css={styles.link}
          component={Link}
          href={next.route}
          variant="quiet"
        >
          <Grid item>
            <Typography2 component="p" variant="bodySecondary" color="body" align="right">
              Next
            </Typography2>
            <Typography2 component="p" variant="bodyPrimary" color="highlightBlue" align="right">
              {next.label}
            </Typography2>
          </Grid>
          <Grid item css={styles.nextIcon}>
            <ChevronNextIcon />
          </Grid>
        </Grid>
      )}
    </Grid>
  );
};

export default Navigation;
